import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState,
    CopperButton, SlateButton, TextInput, NumberInput, Field, Modal, Pill,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { TrendingUp, Plus, Trash2, AlertTriangle } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";

export default function SteelPriceHistory() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const canAdd = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");
    const canDelete = hasRole(user, "CORPORATE_ADMIN");

    const [form, setForm] = useState({
        rate_month: new Date().toISOString().slice(0, 7),
        hr_le_10mm: 0, hr_gt_10mm: 0, cr_sheet: 0, scrap_auction: 0,
        source: "SIAM", notes: "",
    });

    const load = () =>
        api.get("/steel-price-history")
            .then((r) => setRows(r.data))
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const add = async () => {
        setBusy(true);
        try {
            await api.post("/steel-price-history", {
                rate_month: form.rate_month,
                hr_le_10mm: parseFloat(form.hr_le_10mm) || 0,
                hr_gt_10mm: parseFloat(form.hr_gt_10mm) || 0,
                cr_sheet: parseFloat(form.cr_sheet) || 0,
                scrap_auction: parseFloat(form.scrap_auction) || 0,
                source: form.source, notes: form.notes,
            });
            toast.success("Added");
            setOpen(false);
            setForm({ ...form, rate_month: new Date().toISOString().slice(0, 7), notes: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const remove = async (h) => {
        if (!window.confirm(`Delete entry for ${h.rate_month}?`)) return;
        try { await api.delete(`/steel-price-history/${h.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const hasSimulatedRows = (rows || []).some((r) => (r.source || "").includes("Simulated"));

    // Simple inline SVG line chart for HR ≤ 10mm trend
    const chartData = (rows || []).slice().sort((a, b) => (a.rate_month || "").localeCompare(b.rate_month || ""));
    const max = Math.max(1, ...chartData.map((r) => Math.max(r.hr_le_10mm || 0, r.hr_gt_10mm || 0, r.cr_sheet || 0)));
    const W = 720, H = 180, P = 30;
    const xStep = chartData.length > 1 ? (W - 2 * P) / (chartData.length - 1) : 0;
    const path = (key) =>
        chartData.map((r, i) => {
            const x = P + i * xStep;
            const y = H - P - ((r[key] || 0) / max) * (H - 2 * P);
            return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(" ");

    return (
        <div data-testid="steel-price-history-page">
            <PageHeader
                eyebrow="REFERENCE DATA"
                title="Steel Price History"
                subtitle="Monthly SIAM benchmark rates used in Settlement negotiations. Maintain manually after each SIAM release."
                actions={canAdd && (
                    <CopperButton onClick={() => setOpen(true)} data-testid="sph-add-btn">
                        <Plus className="h-4 w-4" /> Add Month
                    </CopperButton>
                )}
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {hasSimulatedRows && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-4 flex items-start gap-2.5">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-[12.5px] text-amber-900">
                        <span className="font-semibold">Sample data.</span> The real SIAM index feed isn&apos;t
                        connected yet, so entries marked &quot;SIAM (Simulated)&quot; below are a realistic
                        preview trend, not actual market data. Replace them with real monthly releases as they
                        become available; simulated rows are safe to delete once real data starts.
                    </div>
                </div>
            )}

            {rows && rows.length === 0 && (
                <EmptyState
                    icon={TrendingUp}
                    title="No SIAM data yet"
                    description={canAdd ? "Add monthly SIAM rates to power benchmarking in Settlements." : "Awaiting Finance to upload SIAM data."}
                    action={canAdd && <CopperButton onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add First Entry</CopperButton>}
                />
            )}

            {rows && rows.length > 0 && (
                <>
                    {chartData.length >= 2 && (
                        <Card className="p-4 mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="eyebrow text-slate-500">PRICE TREND (₹/kg)</div>
                                <div className="flex items-center gap-3 text-[11px]">
                                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(var(--copper))]"/>HR ≤ 10mm</span>
                                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500"/>HR &gt; 10mm</span>
                                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500"/>CR Sheet</span>
                                </div>
                            </div>
                            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
                                {/* baseline */}
                                <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#e2e8f0" strokeWidth="1" />
                                <path d={path("hr_le_10mm")} fill="none" stroke="hsl(18, 65%, 47%)" strokeWidth="2" />
                                <path d={path("hr_gt_10mm")} fill="none" stroke="#10b981" strokeWidth="2" />
                                <path d={path("cr_sheet")} fill="none" stroke="#3b82f6" strokeWidth="2" />
                                {chartData.map((r, i) => (
                                    <g key={i}>
                                        <text x={P + i * xStep} y={H - P + 14} fontSize="9" fill="#64748b" textAnchor="middle" fontFamily="monospace">{r.rate_month}</text>
                                    </g>
                                ))}
                            </svg>
                        </Card>
                    )}

                    <Card>
                        <table className="w-full table-rowhover">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60">
                                    <th className="eyebrow text-left py-2 px-3">Month</th>
                                    <th className="eyebrow text-right py-2 px-3">HR ≤ 10mm</th>
                                    <th className="eyebrow text-right py-2 px-3">HR &gt; 10mm</th>
                                    <th className="eyebrow text-right py-2 px-3">CR Sheet</th>
                                    <th className="eyebrow text-right py-2 px-3">Scrap Auction</th>
                                    <th className="eyebrow text-left py-2 px-3">Source</th>
                                    <th className="eyebrow text-left py-2 px-3">Notes</th>
                                    <th className="eyebrow text-right py-2 px-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-slate-100" data-testid={`sph-row-${r.rate_month}`}>
                                        <td className="py-1.5 px-3 font-mono font-semibold text-[13px]">{r.rate_month}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{fmtINR(r.hr_le_10mm)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{fmtINR(r.hr_gt_10mm)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{fmtINR(r.cr_sheet)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{fmtINR(r.scrap_auction)}</td>
                                        <td className="py-1.5 px-3 text-[12px] text-slate-600">
                                            <div className="flex items-center gap-1.5">
                                                {r.source}
                                                {(r.source || "").includes("Simulated") && (
                                                    <Pill tone="amber" className="text-[9.5px]">Sample</Pill>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-1.5 px-3 text-[11px] text-slate-500">{r.notes || "—"}</td>
                                        <td className="py-1.5 px-3 text-right">
                                            {canDelete && (
                                                <button onClick={() => remove(r)} className="text-red-600 hover:bg-red-50 h-7 w-7 rounded-md inline-flex items-center justify-center">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </>
            )}

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Add SIAM Monthly Entry"
                size="lg"
                footer={
                    <>
                        <SlateButton onClick={() => setOpen(false)} variant="ghost">Cancel</SlateButton>
                        <CopperButton onClick={add} disabled={busy || !form.rate_month} data-testid="sph-save-btn">Save Entry</CopperButton>
                    </>
                }
            >
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Month (YYYY-MM)" required>
                        <TextInput value={form.rate_month} onChange={(e) => upd("rate_month", e.target.value)} placeholder="2026-01" data-testid="sph-month-input" />
                    </Field>
                    <Field label="Source">
                        <TextInput value={form.source} onChange={(e) => upd("source", e.target.value)} />
                    </Field>
                    <Field label="HR ≤ 10mm (₹/kg)"><NumberInput value={form.hr_le_10mm} onChange={(e) => upd("hr_le_10mm", e.target.value)} /></Field>
                    <Field label="HR > 10mm (₹/kg)"><NumberInput value={form.hr_gt_10mm} onChange={(e) => upd("hr_gt_10mm", e.target.value)} /></Field>
                    <Field label="CR Sheet (₹/kg)"><NumberInput value={form.cr_sheet} onChange={(e) => upd("cr_sheet", e.target.value)} /></Field>
                    <Field label="Scrap Auction (₹/kg)"><NumberInput value={form.scrap_auction} onChange={(e) => upd("scrap_auction", e.target.value)} /></Field>
                    <Field label="Notes" className="col-span-2"><TextInput value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Source URL, comments…" /></Field>
                </div>
            </Modal>
        </div>
    );
}
